<template>
  <multiselect
    ref="multiselect"
    v-bind="{ ...$attrs, ...context.attributes }"
    :class="`formulate-input-element formulate-input-element--${context.type} input-${size}`"
    :custom-label="customLabel"
    :data-type="context.type"
    :internal-search="false"
    :loading="isLoading"
    :options="options"
    :optionHeight="40"
    :maxHeight="limit * 40"
    :track-by="trackBy"
    :multiple="multiple"
    :placeholder="placeholder"
    :value="inputValueGetter()"
    @input="inputValueSetter"
    @close="handleClose"
    @open="handleOpen"
    @pagination="handlePagination"
    @search-change="handleSearchChange"
  >
    <!-- Custom dropdown toggle -->
    <template #caret="{ toggle }">
      <div class="mc-caret__icon" @mousedown.prevent.stop="toggle">
        <i class="icon-outline_chevron-down" v-if="!isOpenDropdown"></i>
        <i class="icon-outline_close" v-else></i>
      </div>
    </template>

    <!-- Slot: after the list of options, when this element is in view, trigger pagination event -->
    <template #afterList>
      <div v-if="isLoading" class="px-2 py-2 d-flex gap-3 flex-column">
        <b-skeleton v-for="(i, idx) in ['100', '100']" :width="i" :key="idx"></b-skeleton>
      </div>
      <div v-observe-visibility="handleElementObserved" style="height: 10px" />
    </template>

    <template #selection="{ isOpen }">
      <i class="mc-fa icon-fill_search me-1" v-if="isOpen"></i>
    </template>

    <!--  For select options with image -->
    <template slot="singleLabel" slot-scope="options" v-if="hasImage || hasAnyIcons">
      <div class="d-flex flex-row align-items-center">
        <img
          class="option__image"
          :src="options.option.img"
          :alt="options.option.value"
          :style="options.option.style"
        />
        <div class="option__desc ps-3">
          <span class="option__title">{{ options.option.label }}</span>
        </div>
      </div>
    </template>
    <template slot="option" slot-scope="options" v-if="hasImage || hasAnyIcons">
      <div class="d-flex flex-row align-items-center">
        <img
          class="option__image"
          :src="options.option.img"
          :alt="options.option.value"
          :style="options.option.style"
        />
        <div class="option__desc ps-3">
          <span class="option__title">{{ options.option.label }}</span>
        </div>
      </div>
    </template>
  </multiselect>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { debounce } from '@/mixin/helper'

export default {
  components: {
    Multiselect,
  },
  props: {
    context: {
      type: Object,
      required: true,
    },
    initialObject: {
      type: [Object, Array],
      required: false,
    },
    trackBy: {
      type: String,
      default: 'id',
    },
    optionLabelFn: {
      type: Function,
      default: ({ item }) => item,
    },
    optionsGetter: {
      type: [Function, null],
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'md',
    },
    hasImage: {
      type: Boolean,
      default: false,
    },
    overrideFilters: {
      type: Object,
      default: () => {},
    },
  },
  emits: ['openDropdown'],
  data() {
    return {
      placeholder: this.context.attributes?.placeholder,
      options: [],
      isLoading: false,
      isOpenDropdown: false,
      limit: 5,
      page: null,
      totalPage: null,
      search: '',
    }
  },
  computed: {
    optionsContainerRef() {
      return this.$refs?.multiselect?.$refs?.list
    },
    hasNextPage() {
      // If totalCount is null The data has not been fetched yet
      // so we return true to get the actual total count from the first request
      if (this.totalCount == null) return true
      if (this.page == null) return true
      return this.page * this.limit < this.totalCount
    },
    hasAnyIcons() {
      if (this.hasImage) return true
      else return this.$refs?.multiselect?.$parent?.$parent?.$attrs?.hasImage
    },
  },
  mounted() {
    document.addEventListener('scroll', this.repositionDropDown, true)
  },
  destroyed() {
    document.removeEventListener('scroll', this.repositionDropDown)
  },
  methods: {
    async getData(args = { reset: false }) {
      if (this.isLoading) return
      if (!this.optionsGetter) return
      if (!args.reset && !this.hasNextPage) return

      this.isLoading = true

      let nextPage = this.page + 1
      if (args.reset) {
        nextPage = 1
        this.options = []
      }
      let params = {
        limit: this.limit,
        page: nextPage,
      }

      if (this.overrideFilters?.filters) params['filters'] = ''
      if (this.search !== '') params['search'] = this.search
      let replacedParams = {}
      for (const key in this.overrideFilters) {
        if (params.hasOwnProperty(key)) {
          replacedParams[key] = params[key]
        } else {
          if (params[this.overrideFilters[key]] !== undefined)
            replacedParams[key] = params[this.overrideFilters[key]]

          /** query param using filters,
           *  using eq for the exact value, for example -> filters=name:eq:Mceasy
           *  using like, for example -> filters=name:like:Mce */
          if (key === 'filters' && Object.keys(this.overrideFilters).includes('filters'))
            replacedParams[key] = `${this.overrideFilters['filtersQuery']}:like:${this.search}`
        }
      }
      if (this.overrideFilters !== undefined) params = replacedParams
      const res = await this.optionsGetter(params)
      if (!res.data) return

      if (res.metadata) {
        this.totalPage = res.metadata.total_page
        this.totalCount = res.metadata.total_count
        this.page = res.metadata.page
        this.options = args.reset
          ? res.data.paginated_result
          : [...this.options, ...res.data.paginated_result]
      } else {
        this.totalPage = res.total_pages
        this.totalCount = res.total_count
        this.page = res.page
        this.options = args.reset ? res.data : [...this.options, ...res.data]
      }

      await this.$nextTick()
      if (res.has_more && this.overrideFilters && this.page === 1) {
        this.isLoading = false
        await this.getData({
          page: 2,
          size: this.limit,
        })
        if (typeof this.optionsContainerRef !== 'function') return
        this.optionsContainerRef.scrollTo({
          top: this.optionsContainerRef.scrollHeight,
          behavior: 'smooth',
        })
      }
      this.isLoading = false
    },
    customLabel(item) {
      if (!this.optionLabelFn) return item

      return this.optionLabelFn({ item, options: this.options })
    },
    // Since we only track the ID, we still need to pass the whole object
    // to vue-multiselect so every selection is detected
    inputValueGetter() {
      let _value = this.multiple ? [] : ''

      const selected = this.context.model
      // make initial object an array to be appended in the options
      const _initialObject = this.multiple ? this.initialObject || [] : [this.initialObject]
      // if there are no options, use initial object above
      const objectSource = this.options.length == 0 ? _initialObject : this.options

      // Display the selected value based on the object source,
      // if the select input supports multiple, loop over it first
      for (let item of objectSource) {
        if (!item) continue

        if (this.multiple && selected.includes(item[this.trackBy])) {
          _value.push(item)
        }

        // If single
        else if (selected === item[this.trackBy]) {
          _value = item
        }
      }

      return _value
    },
    // Since we usually only track the ID, we need to parse the option
    // selected by vue-multiselect before setting it in the model
    inputValueSetter(input) {
      const parsed = Array.isArray(input)
        ? input.map((i) => i[this.trackBy])
        : !input
        ? input
        : input[this.trackBy]

      this.context.model = parsed

      // Emit objek yang dipilih biar bisa dipakai di luar
      this.$emit('update:selectedObject', input)
    },
    async handleOpen() {
      this.$emit('openDropdown', true)
      this.repositionDropDown()
      this.placeholder = 'Search'
      await this.getData({ reset: true })
      await this.$nextTick()
      this.isOpenDropdown = true
    },
    handleClose() {
      this.$emit('openDropdown', false)
      this.placeholder = this.context.attributes?.placeholder
      this.isLoading = false
      this.isOpenDropdown = false
      this.context.blurHandler()
    },
    async handlePagination() {
      if (this.isLoading) return
      if (this.optionsGetter().hasMore === false) return
      await this.getData()
    },
    // This function will get called when:
    // - Search value is changed
    // - Selecting an option from search (which will reset the search value)
    handleSearchChange: debounce(async function (search) {
      if (this.isLoading) return

      this.search = search
      await this.$nextTick()

      // If the dropdown is NOT open,
      // it means that user already selected an option from search
      // so we don't need to reset the options
      // since it is needed for displaying the value
      if (!this.isOpenDropdown) return

      await this.getData({ reset: true })
    }, 500),
    handleElementObserved(isVisible) {
      if (!isVisible) return

      this.handlePagination()

      // Scroll to bottom when pagination is triggered
      this.$nextTick().then(() => {
        if (typeof this.optionsContainerRef !== 'function') return
        this.optionsContainerRef.scrollTo({
          top: this.optionsContainerRef.scrollHeight,
          behavior: 'smooth',
        })
      })
    },
    // Make dropdown display fixed to avoid being cut off by parent container
    // and make them stick to parent while scrolling
    repositionDropDown() {
      if (!this.optionsContainerRef?.style) return
      const { top, height, left } = this.$el.getBoundingClientRect()
      this.optionsContainerRef.style.width = `${this.$el.clientWidth}px`
      this.optionsContainerRef.style.position = 'fixed'
      this.optionsContainerRef.style.bottom = 'auto'
      this.optionsContainerRef.style.top = `${top + height}px`
      this.optionsContainerRef.style.left = `${left}px`
    },
    // Methods to be called externally is in PascalCase format
    // methods to be called by parent as side effect when other fields changes
    // e.g. Delivery Form -> customer_notes should changed when product_id changed
    ResetProperties() {
      this.options = []
      this.isLoading = false
      this.limit = 5
      this.page = null
      this.totalPage = null
      this.optionsSearch = ''
    },
  },
}
</script>
